import { useSelector } from "react-redux";
import { IoClose } from "react-icons/io5";
import { FaCheckCircle, FaPauseCircle, FaHourglassHalf, FaTimesCircle } from "react-icons/fa";
import { FiFlag } from "react-icons/fi";
import { useState } from "react";

const statusIcons = {
  completed: { icon: <FaCheckCircle className="text-green-500 " />, label: "Completed" },
  paused: { icon: <FaPauseCircle className="text-yellow-500" />, label: "Paused" },
  in_progress: { icon: <FaHourglassHalf className="text-orange-500" />, label: "In Progress" },
  canceled: { icon: <FaTimesCircle className="text-red-500" />, label: "Canceled" },
};

const ViewProject = ({ project, onClose, editProject, deleteProject, setMileStoneId, setViewMileStonePopup }) => {
  const { theme } = useSelector((state) => state.theme);
  const isDark = theme === "dark";
  
  const [isDeleting , setIsDeleting] = useState(false);

  const handleDeleteProject = async (item) => {
    setIsDeleting(true);
    try{
    await deleteProject(item);
    }
    catch(err){

    }finally{

      setIsDeleting(false);
      onClose(false)
    }
  }

  function countCompletedMilestones(projectData) {
    if (!projectData.milestones || !Array.isArray(projectData.milestones)) {
        return 0;
    }
    
    return projectData.milestones.filter(milestone => 
        milestone.status === 'completed'
    ).length;
}

function calculateProjectProgress(projectData) {
    if (!projectData.milestones || !Array.isArray(projectData.milestones)) {
        return 0;
    }
    
    const totalMilestones = projectData.milestones.length;
    if (totalMilestones === 0) {
        return 0;
    }
    
    const completedMilestones = countCompletedMilestones(projectData);
    return Math.round((completedMilestones / totalMilestones) * 100);
}

  return (
    <div className="fixed inset-0 bg-black/40 z-[1000] flex justify-center items-center px-4">
      <div className={`max-w-screen-sm w-full rounded-2xl shadow-lg overflow-hidden relative transition-all
        ${isDark ? "bg-[#1e1e1e] text-white" : "bg-white text-black"}`}>
        
        {/* Close Button */}
        <button onClick={()=>onClose(false)} className={`absolute top-3 right-3 text-xl hover:text-red-600 rounded-full p-2 ${theme === 'dark' ?'bg-gray-800':'bg-gray-200'}`}>
          <IoClose className="w-6 h-6"/>
        </button>

        {/* Modal Content */}
        <div className="p-6 space-y-4">
          {/* Title */}
          <h2 className="text-xl font-semibold">{project.title}</h2>

          {/* Info */}
          <div className="grid grid-cols-3 gap-4 text-base">
            <div>
              <p className="text-gray-500">Budget</p>
              <p className="font-medium">{project.total_budget} USD</p>
            </div>
            <div>
              <p className="text-gray-500">Status</p>
              <div className="flex items-center gap-1 text-base font-medium">
                     {statusIcons[project.status]?.icon} 
                    <span>{statusIcons[project?.status]?.label}</span>
                  </div>
            </div>
            <div>
              <p className="text-gray-500">Duration</p>
              <p className="whitespace-nowrap font-medium ">{project.start_date} to {project.end_date}</p>
            </div>
          </div>

          {/* Progress */}
          <div>
            <p className="text-gray-500 mb-1">Project Progress</p>
            <div className={`w-full h-2 rounded-full ${isDark ? "bg-gray-700" : "bg-gray-400"}`}>
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#CE0927] to-[#7E0105] transition-all"
                style={{ width: `${calculateProjectProgress(project)}%` }}
              />
            </div>
            <p className="text-sm mt-1">{Math.floor(calculateProjectProgress(project))}% Completed</p>
          </div>

          {/* Milestones */}
          <div>
            <p className="text-gray-500 mb-1">Milestones</p>
            <ul className="space-y-2">
              {project.milestones.map((m, index) => (
                <li key={index} className={`flex justify-between items-center px-3 py-2 rounded-lg
                  ${isDark ? "bg-[#2e2e2e]" : "bg-gray-100"}`} onClick={()=>{
                    setMileStoneId(m?.id)
                    setViewMileStonePopup(true)
                  }}>
                  <span className="flex items-center gap-2"><FiFlag className='w-6 h-6'/> {m.title}</span>
                  <div className="flex items-center gap-1 text-sm">
                    {statusIcons[m.status]?.icon}
                    <span>{statusIcons[m.status]?.label}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Description */}
          <div>
            <p className="text-gray-500 mb-1">Project Description</p>
            <p className={`text-sm p-3 rounded-md ${isDark ? "bg-[#2e2e2e]" : "bg-[#D8D8D8]"}`} dangerouslySetInnerHTML={{ __html: project.description}}>
            </p>
          </div>

          {/* Buttons */}
          <div className="flex justify-center gap-6 items-center mt-4 w-full">
            <button
              onClick={()=>handleDeleteProject(project)} disabled={isDeleting}
              className="px-4 py-2 rounded-full w-1/3 border border-gray-300 hover:border-red-600 text-sm"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
            <button
              onClick={()=>editProject(project)}
              className="px-4 py-2 w-1/3 rounded-lg heroButton"
            >
              Edit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewProject;
